import {
  Outlet,
  createRootRoute,
  HeadContent,
  Scripts,
  Link,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth-context";
import { installServerFnAuth } from "@/lib/server-fn-auth";
import { RouteChangeLoader } from "@/components/app/RouteChangeLoader";
import { initSentry } from "@/lib/sentry";

if (typeof window !== "undefined") {
  installServerFnAuth();
  initSentry();
}

import appCss from "../styles.css?url";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

function NotFoundComponent() {
  return (
    <section className="notfound-404">
      <style>{`
        .notfound-404 {
          padding: 40px 0;
          background: #fff;
          font-family: 'Arvo', serif;
          min-height: 100vh;
        }
        .notfound-404 .nf-container {
          max-width: 960px;
          margin: 0 auto;
          padding: 0 15px;
          text-align: center;
        }
        .notfound-404 .nf-bg {
          background-image: url(https://cdn.dribbble.com/users/285475/screenshots/2083086/dribbble_1.gif);
          height: 400px;
          background-position: center;
          background-repeat: no-repeat;
        }
        .notfound-404 .nf-bg h1 {
          font-size: 80px;
          margin: 0;
          padding-top: 20px;
          color: #000;
        }
        .notfound-404 .nf-content { margin-top: -50px; }
        .notfound-404 .nf-content h3 { font-size: 28px; color: #000; }
        .notfound-404 .nf-content p { color: #333; }
        .notfound-404 .nf-link {
          color: #fff !important;
          padding: 10px 20px;
          background: hsl(var(--primary, 262 83% 58%));
          margin: 20px 0;
          display: inline-block;
          text-decoration: none;
          border-radius: 6px;
        }
      `}</style>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Arvo" />
      <div className="nf-container">
        <div className="nf-bg">
          <h1>404</h1>
        </div>
        <div className="nf-content">
          <h3>Look like you're lost</h3>
          <p>the page you are looking for not avaible!</p>
          <Link to="/" className="nf-link">Go to Home</Link>
        </div>
      </div>
    </section>
  );
}


export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "CretikPanda Workspace" },
      {
        name: "description",
        content: "Internal team management and performance dashboard.",
      },
      { property: "og:title", content: "CretikPanda Workspace" },
      { name: "twitter:title", content: "CretikPanda Workspace" },
      { name: "description", content: "Team Pulse Dashboard is a SaaS application for unified team management and performance tracking." },
      { property: "og:description", content: "Team Pulse Dashboard is a SaaS application for unified team management and performance tracking." },
      { name: "twitter:description", content: "Team Pulse Dashboard is a SaaS application for unified team management and performance tracking." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/d024f061-e0a8-4616-831d-43915363f54f/id-preview-603faa8d--0e03a0ef-2f71-40d8-8c20-1c8ad1765448.lovable.app-1777997614937.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/d024f061-e0a8-4616-831d-43915363f54f/id-preview-603faa8d--0e03a0ef-2f71-40d8-8c20-1c8ad1765448.lovable.app-1777997614937.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap",
      },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Outlet />
        <RouteChangeLoader />
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  );
}
